import React, { useCallback, useState } from 'react';
import {
  LayoutTemplate,
  FileText,
  Table2,
  Sigma,
  List,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  X,
  Upload,
  Trash2,
  Receipt,
} from 'lucide-react';
import {
  defaultTemplateConfig,
  IMAGE_POSITION_OPTIONS,
  IMAGE_UPLOAD_MAX_BYTES,
  PDF_FONT_OPTIONS,
  type InvoiceTemplateConfig,
} from './invoiceTemplateConfig';
import { fastapiService } from '../../services/fastapiService';
import { InvoiceTemplatePreview } from './InvoiceTemplatePreview';
import { InvoiceTemplateHtmlEditor } from './InvoiceTemplateHtmlEditor';

type TabId = 'general' | 'header_footer' | 'transaction_details' | 'table' | 'total' | 'other';

type Props = {
  isEnterprise: boolean;
  mode: 'create' | 'edit';
  formName: string;
  setFormName: (v: string) => void;
  formDescription: string;
  setFormDescription: (v: string) => void;
  formSlug: string;
  setFormSlug: (v: string) => void;
  formWorkspaceId: string;
  setFormWorkspaceId: (v: string) => void;
  formIsDefault: boolean;
  setFormIsDefault: (v: boolean) => void;
  formIsActive: boolean;
  setFormIsActive: (v: boolean) => void;
  config: InvoiceTemplateConfig;
  setConfig: React.Dispatch<React.SetStateAction<InvoiceTemplateConfig>>;
  saving: boolean;
  onSave: () => void;
  onClose: () => void;
};

function AccordionRow({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2.5 overflow-hidden rounded-lg border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition hover:bg-slate-50/90"
      >
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
        <span>
          <span className="block text-sm font-semibold text-slate-800">{title}</span>
          {subtitle && <span className="mt-0.5 block text-xs font-normal text-slate-500">{subtitle}</span>}
        </span>
      </button>
      {open && <div className="border-t border-slate-100 bg-slate-50/30 px-3 py-3 text-sm">{children}</div>}
    </div>
  );
}

function ImageDrop({
  maxBytes,
  onFile,
  onClear,
  hasImage,
  compact,
}: {
  maxBytes: number;
  onFile: (file: File) => void | Promise<void>;
  onClear: () => void;
  hasImage: boolean;
  compact?: boolean;
}) {
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setErr(null);
    try {
      if (f.size > maxBytes) {
        const label = maxBytes >= 1024 * 1024 ? `${maxBytes / (1024 * 1024)} MB` : `${Math.round(maxBytes / 1024)} KB`;
        throw new Error(`Max size: ${label}`);
      }
      setUploading(true);
      await Promise.resolve(onFile(f));
    } catch (er) {
      setErr(er instanceof Error ? er.message : 'Invalid file');
    } finally {
      setUploading(false);
    }
  };
  const maxLabel = maxBytes >= 1024 * 1024 ? `${maxBytes / (1024 * 1024)} MB` : `${Math.round(maxBytes / 1024)} KB`;
  return (
    <div>
      <label
        className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-sky-200 bg-sky-50/50 transition hover:border-sky-400 hover:bg-sky-50 ${
          compact ? 'px-3 py-3' : 'px-4 py-5'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        <Upload className={`shrink-0 text-sky-600 ${compact ? 'mb-1 h-6 w-6' : 'mb-2 h-7 w-7'}`} />
        <span className={`font-medium text-sky-900 ${compact ? 'text-xs' : 'text-sm'}`}>
          {uploading ? 'Uploading to cloud storage…' : 'Drop image or click to upload'}
        </span>
        <span className="mt-0.5 text-center text-[11px] leading-snug text-slate-500">
          Max {maxLabel} · PNG, JPG, GIF, BMP · stored under Admin/ in your bucket
        </span>
        <input type="file" accept="image/*" className="sr-only" onChange={onChange} disabled={uploading} />
      </label>
      {err && <p className="mt-1 text-xs text-red-600">{err}</p>}
      {hasImage && (
        <button type="button" onClick={onClear} className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline">
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </button>
      )}
    </div>
  );
}

const TABS: { id: TabId; label: string; short: string; icon: React.ReactNode }[] = [
  { id: 'general', label: 'General', short: 'General', icon: <LayoutTemplate className="h-4 w-4 shrink-0" /> },
  { id: 'header_footer', label: 'Header & Footer', short: 'Header', icon: <FileText className="h-4 w-4 shrink-0" /> },
  { id: 'transaction_details', label: 'Transaction', short: 'Txn', icon: <Receipt className="h-4 w-4 shrink-0" /> },
  { id: 'table', label: 'Table', short: 'Table', icon: <Table2 className="h-4 w-4 shrink-0" /> },
  { id: 'total', label: 'Total', short: 'Total', icon: <Sigma className="h-4 w-4 shrink-0" /> },
  { id: 'other', label: 'Other', short: 'Other', icon: <List className="h-4 w-4 shrink-0" /> },
];

export const InvoiceTemplateEditor: React.FC<Props> = ({
  isEnterprise,
  mode,
  formName,
  setFormName,
  formDescription,
  setFormDescription,
  formSlug,
  setFormSlug,
  formWorkspaceId,
  setFormWorkspaceId,
  formIsDefault,
  setFormIsDefault,
  formIsActive,
  setFormIsActive,
  config,
  setConfig,
  saving,
  onSave,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [previewKey, setPreviewKey] = useState(0);
  /** Only one General accordion open at a time; tap again to collapse */
  const [genSection, setGenSection] = useState<'doc' | 'pay' | 'tp' | 'font' | 'bg' | null>('tp');
  const [hfOpen, setHfOpen] = useState({ header: true, footer: false });
  const [txnOpen, setTxnOpen] = useState(true);
  const [tblOpen, setTblOpen] = useState(true);
  const [totOpen, setTotOpen] = useState(true);
  const [othOpen, setOthOpen] = useState(true);
  const [publishingOpen, setPublishingOpen] = useState(mode === 'create');

  const toggleGen = (key: 'doc' | 'pay' | 'tp' | 'font' | 'bg') => {
    setGenSection((cur) => (cur === key ? null : key));
  };

  const isGenOpen = (key: 'doc' | 'pay' | 'tp' | 'font' | 'bg') => genSection === key;

  const accentBg = isEnterprise ? 'bg-emerald-600' : 'bg-teal-600';
  const accentTab = isEnterprise ? 'bg-emerald-600 text-white' : 'bg-teal-600 text-white';

  const mergeFooter = useCallback(
    (partial: Partial<InvoiceTemplateConfig['header_footer']['footer']>) => {
      setConfig((p) => ({
        ...p,
        header_footer: { ...p.header_footer, footer: { ...p.header_footer.footer, ...partial } },
      }));
    },
    [setConfig],
  );

  const mergeHeader = useCallback(
    (partial: Partial<InvoiceTemplateConfig['header_footer']['header']>) => {
      setConfig((p) => ({
        ...p,
        header_footer: { ...p.header_footer, header: { ...p.header_footer.header, ...partial } },
      }));
    },
    [setConfig],
  );

  const mergeGeneralBg = useCallback(
    (partial: Partial<InvoiceTemplateConfig['general']['background']>) => {
      setConfig((p) => ({
        ...p,
        general: { ...p.general, background: { ...p.general.background, ...partial } },
      }));
    },
    [setConfig],
  );

  const mergeGeneralFont = useCallback(
    (partial: Partial<InvoiceTemplateConfig['general']['font']>) => {
      setConfig((p) => ({
        ...p,
        general: { ...p.general, font: { ...p.general.font, ...partial } },
      }));
    },
    [setConfig],
  );

  const mergeGeneralTp = useCallback(
    (partial: Partial<InvoiceTemplateConfig['general']['template_properties']>) => {
      setConfig((p) => ({
        ...p,
        general: {
          ...p.general,
          template_properties: { ...p.general.template_properties, ...partial },
        },
      }));
    },
    [setConfig],
  );

  const mergeMargins = useCallback(
    (partial: Partial<InvoiceTemplateConfig['general']['template_properties']['margins_inches']>) => {
      setConfig((p) => ({
        ...p,
        general: {
          ...p.general,
          template_properties: {
            ...p.general.template_properties,
            margins_inches: { ...p.general.template_properties.margins_inches, ...partial },
          },
        },
      }));
    },
    [setConfig],
  );

  const mergeTransactionDetails = useCallback(
    (partial: Partial<InvoiceTemplateConfig['transaction_details']>) => {
      setConfig((p) => ({
        ...p,
        transaction_details: { ...p.transaction_details, ...partial },
      }));
    },
    [setConfig],
  );

  const mergeTable = useCallback(
    (partial: Partial<InvoiceTemplateConfig['table']>) => {
      setConfig((p) => ({
        ...p,
        table: { ...p.table, ...partial },
      }));
    },
    [setConfig],
  );

  const mergeTotal = useCallback(
    (partial: Partial<InvoiceTemplateConfig['total']>) => {
      setConfig((p) => ({
        ...p,
        total: { ...p.total, ...partial },
      }));
    },
    [setConfig],
  );

  const mergeOther = useCallback(
    (partial: Partial<InvoiceTemplateConfig['other']>) => {
      setConfig((p) => ({
        ...p,
        other: { ...p.other, ...partial },
      }));
    },
    [setConfig],
  );

  const td = config.transaction_details;
  const tbl = config.table;
  const tot = config.total;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-slate-200/95">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-300 bg-white px-4 py-3 shadow-sm">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-slate-900">
            {mode === 'create' ? 'New template' : 'Edit template'}
            {formName.trim() ? <span className="font-normal text-slate-500"> · {formName.trim()}</span> : null}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">Changes apply on Save. Preview updates as you edit.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            Theme
            <select
              value={config.color_theme}
              onChange={(e) => {
                const v = e.target.value;
                setConfig((p) => {
                  let primary_color = p.primary_color;
                  if (v === 'sws_orange') primary_color = '#ea580c';
                  else if (v === 'minimal') primary_color = '#18181b';
                  else primary_color = defaultTemplateConfig().primary_color;
                  return { ...p, color_theme: v, primary_color };
                });
                setPreviewKey((k) => k + 1);
              }}
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
            >
              <option value="default">Default</option>
              <option value="sws_orange">SWS Orange</option>
              <option value="minimal">Minimal</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => setPreviewKey((k) => k + 1)}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            title="Redraw preview (e.g. after changing images)"
          >
            <RefreshCw className="h-4 w-4" />
            Preview
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={onSave}
            className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold text-white ${accentBg} hover:opacity-95 disabled:opacity-50`}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <aside className="flex w-full shrink-0 flex-row gap-1 overflow-x-auto overflow-y-hidden bg-slate-900 px-2 py-2 text-sm text-slate-300 lg:w-[13.5rem] lg:flex-col lg:gap-0.5 lg:overflow-y-auto lg:overflow-x-hidden lg:px-2 lg:py-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`flex min-w-[7.5rem] items-center gap-2 rounded-md px-2 py-2 text-left text-[13px] font-medium transition lg:min-w-0 lg:px-2.5 lg:py-2.5 ${
                activeTab === t.id ? accentTab : 'hover:bg-slate-800/80 hover:text-white'
              }`}
            >
              {t.icon}
              <span className="leading-snug lg:hidden">{t.short}</span>
              <span className="hidden leading-snug lg:inline">{t.label}</span>
            </button>
          ))}
        </aside>

        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-slate-100">
          <div className="sticky top-0 z-10 border-b border-slate-200/90 bg-slate-100/90 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-slate-100/75">
            <div className="mx-auto max-w-3xl space-y-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <label className="min-w-0 flex-1 text-xs font-medium text-slate-700">
                  Template name
                  <input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. Lite, Tax invoice A4"
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm font-medium text-slate-900 shadow-sm"
                  />
                </label>
                <label className="min-w-0 flex-1 text-xs font-medium text-slate-700">
                  Description
                  <input
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Internal note (optional)"
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm shadow-sm"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => setPublishingOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 sm:w-auto sm:justify-center"
              >
                <span>{publishingOpen ? 'Hide' : 'Show'} slug, workspace, default &amp; active</span>
                {publishingOpen ? <ChevronDown className="h-4 w-4 shrink-0 opacity-60" /> : <ChevronRight className="h-4 w-4 shrink-0 opacity-60" />}
              </button>
              {publishingOpen && (
                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {mode === 'create' && (
                      <>
                        <label className="block text-xs font-medium text-slate-700">
                          URL slug (optional)
                          <input
                            value={formSlug}
                            onChange={(e) => setFormSlug(e.target.value)}
                            placeholder="auto-generated if empty"
                            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-2 font-mono text-xs"
                          />
                        </label>
                        <label className="block text-xs font-medium text-slate-700">
                          Workspace ID (optional)
                          <input
                            value={formWorkspaceId}
                            onChange={(e) => setFormWorkspaceId(e.target.value)}
                            placeholder="blank = platform template"
                            className="mt-1 w-full rounded-md border border-slate-300 px-2 py-2 font-mono text-xs"
                          />
                        </label>
                      </>
                    )}
                    {mode === 'edit' && (
                      <p className="text-xs text-slate-500 sm:col-span-2">
                        Slug and workspace are fixed after create. Duplicate the template for a new scope.
                      </p>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 border-t border-slate-100 pt-3">
                    <label className="flex items-center gap-2 text-sm text-slate-800">
                      <input
                        type="checkbox"
                        checked={formIsDefault}
                        onChange={(e) => setFormIsDefault(e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Default for this scope
                    </label>
                    {mode === 'edit' && (
                      <label className="flex items-center gap-2 text-sm text-slate-800">
                        <input
                          type="checkbox"
                          checked={formIsActive}
                          onChange={(e) => setFormIsActive(e.target.checked)}
                          className="rounded border-slate-300"
                        />
                        Active
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 pt-4">
          {activeTab === 'general' && (
            <div className="mx-auto max-w-3xl pb-10">
              <AccordionRow
                title="Document & company text"
                subtitle="Invoice title, company address, tax label, footer legal line, post-payment (paid) display"
                open={isGenOpen('doc')}
                onToggle={() => toggleGen('doc')}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-xs font-medium text-slate-700">
                    Invoice title
                    <input
                      value={config.invoice_title}
                      onChange={(e) => setConfig((p) => ({ ...p, invoice_title: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Company / legal line
                    <input
                      value={config.company_legal_name}
                      onChange={(e) => setConfig((p) => ({ ...p, company_legal_name: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                    Address (footer block)
                    <input
                      value={config.company_address}
                      onChange={(e) => setConfig((p) => ({ ...p, company_address: e.target.value }))}
                      placeholder="e.g. SCO 123-124, Sector 17C, Chandigarh"
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Tax ID label
                    <input
                      value={config.tax_id_label}
                      onChange={(e) => setConfig((p) => ({ ...p, tax_id_label: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                    Footer legal line
                    <textarea
                      value={config.footer_legal_text}
                      onChange={(e) => setConfig((p) => ({ ...p, footer_legal_text: e.target.value }))}
                      rows={2}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={config.show_invoice_as_paid}
                      onChange={(e) => setConfig((p) => ({ ...p, show_invoice_as_paid: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    Show invoice as paid (post-payment — amount paid, zero balance)
                  </label>
                  {config.show_invoice_as_paid && (
                    <label className="block text-xs font-medium text-slate-700">
                      Paid badge label
                      <input
                        value={config.paid_badge_label}
                        onChange={(e) => setConfig((p) => ({ ...p, paid_badge_label: e.target.value }))}
                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                      />
                    </label>
                  )}

                  <label className="flex items-center gap-2 text-sm sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={config.show_tax_breakdown}
                      onChange={(e) => setConfig((p) => ({ ...p, show_tax_breakdown: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    Show tax breakdown in totals
                  </label>
                </div>
              </AccordionRow>

              <AccordionRow
                title="Pay to & thank you"
                subtitle="Bank / UPI details and closing message — formatting toolbar + multi-line editor"
                open={isGenOpen('pay')}
                onToggle={() => toggleGen('pay')}
              >
                <div className="space-y-4">
                  <label className="block text-xs font-medium text-slate-700">
                    Pay to — heading
                    <input
                      value={config.pay_to_heading}
                      onChange={(e) => setConfig((p) => ({ ...p, pay_to_heading: e.target.value }))}
                      placeholder="PAY TO :"
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <div>
                    <p className="text-xs font-medium text-slate-700">Pay to — details</p>
                    <p className="mb-1.5 text-[11px] text-slate-500">
                      Bank name, branch, account, IFSC, UPI, etc. Use plain lines or bold key fields.
                    </p>
                    <InvoiceTemplateHtmlEditor
                      monospace
                      rows={9}
                      value={config.pay_to_content}
                      onChange={(v) => setConfig((p) => ({ ...p, pay_to_content: v }))}
                      placeholder={'State Bank of India\nBranch: Sector 16, Chandigarh\nA/c: …\nIFSC: …'}
                      hint="Toolbar wraps selection in HTML tags. Allowed in preview: bold, italic, lists, line breaks — scripts and attributes are stripped."
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-700">Thank you for your business</p>
                    <p className="mb-1.5 text-[11px] text-slate-500">
                      Shown beside pay-to on the invoice. Leave empty to use the default sentence in preview only.
                    </p>
                    <InvoiceTemplateHtmlEditor
                      rows={5}
                      value={config.thank_you_message}
                      onChange={(v) => setConfig((p) => ({ ...p, thank_you_message: v }))}
                      placeholder="Thank you for your business."
                      hint="Same formatting as Pay to. Empty field falls back to “Thank you for your business.” in the preview."
                    />
                  </div>
                </div>
              </AccordionRow>

              <AccordionRow
                title="Template properties"
                subtitle="Paper size, orientation, margins, payment stub"
                open={isGenOpen('tp')}
                onToggle={() => toggleGen('tp')}
              >
                <div className="space-y-3">
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-700">Paper size</p>
                    <div className="flex flex-wrap gap-3">
                      {(['A5', 'A4', 'Letter'] as const).map((sz) => (
                        <label key={sz} className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="paper"
                            checked={config.general.template_properties.paper_size === sz}
                            onChange={() => mergeGeneralTp({ paper_size: sz })}
                          />
                          {sz}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-700">Orientation</p>
                    <div className="flex gap-4">
                      {(['portrait', 'landscape'] as const).map((o) => (
                        <label key={o} className="flex items-center gap-2 text-sm capitalize">
                          <input
                            type="radio"
                            name="orient"
                            checked={config.general.template_properties.orientation === o}
                            onChange={() => mergeGeneralTp({ orientation: o })}
                          />
                          {o}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-700">Margins (inches)</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {(['top', 'bottom', 'left', 'right'] as const).map((edge) => (
                        <label key={edge} className="block text-xs capitalize text-slate-600">
                          {edge}
                          <input
                            type="number"
                            step={0.05}
                            value={config.general.template_properties.margins_inches[edge]}
                            onChange={(e) => mergeMargins({ [edge]: parseFloat(e.target.value) || 0 })}
                            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config.general.template_properties.include_payment_stub}
                      onChange={(e) => mergeGeneralTp({ include_payment_stub: e.target.checked })}
                    />
                    Include payment stub
                  </label>
                </div>
              </AccordionRow>

              <AccordionRow
                title="Font"
                subtitle="PDF typeface, sizes, and body colors"
                open={isGenOpen('font')}
                onToggle={() => toggleGen('font')}
              >
                <div className="space-y-3">
                  <label className="block text-xs font-medium text-slate-700">
                    PDF font
                    <select
                      value={config.general.font.pdf_font}
                      onChange={(e) => mergeGeneralFont({ pdf_font: e.target.value })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    >
                      {PDF_FONT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="text-xs text-slate-500">
                    Supports English and European languages. Ubuntu also renders the Indian rupee symbol well in many PDF engines.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-xs font-medium text-slate-700">
                      Label color
                      <div className="mt-1 flex gap-2">
                        <input
                          value={config.general.font.label_color}
                          onChange={(e) => mergeGeneralFont({ label_color: e.target.value })}
                          className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-2 font-mono text-sm"
                        />
                        <input
                          type="color"
                          aria-label="Label color"
                          value={/^#[0-9a-f]{6}$/i.test(config.general.font.label_color) ? config.general.font.label_color : '#000000'}
                          onChange={(e) => mergeGeneralFont({ label_color: e.target.value })}
                          className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                        />
                      </div>
                    </label>
                    <label className="block text-xs font-medium text-slate-700">
                      Font color
                      <div className="mt-1 flex gap-2">
                        <input
                          value={config.general.font.font_color}
                          onChange={(e) => mergeGeneralFont({ font_color: e.target.value })}
                          className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-2 font-mono text-sm"
                        />
                        <input
                          type="color"
                          aria-label="Font color"
                          value={/^#[0-9a-f]{6}$/i.test(config.general.font.font_color) ? config.general.font.font_color : '#000000'}
                          onChange={(e) => mergeGeneralFont({ font_color: e.target.value })}
                          className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                        />
                      </div>
                    </label>
                    <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                      Font size (pt)
                      <input
                        type="number"
                        min={6}
                        max={24}
                        value={config.general.font.font_size_pt}
                        onChange={(e) => mergeGeneralFont({ font_size_pt: parseInt(e.target.value, 10) || 8 })}
                        className="mt-1 w-28 rounded border border-slate-300 px-2 py-2 text-sm"
                      />
                      <span className="ml-2 text-xs text-slate-500">pt</span>
                    </label>
                  </div>
                </div>
              </AccordionRow>

              <AccordionRow
                title="Background"
                subtitle="Page wallpaper behind the invoice (optional)"
                open={isGenOpen('bg')}
                onToggle={() => toggleGen('bg')}
              >
                <div className="space-y-3">
                  <ImageDrop
                    compact
                    maxBytes={IMAGE_UPLOAD_MAX_BYTES}
                    hasImage={Boolean(config.general.background.image_data_url || config.general.background.image_url)}
                    onFile={async (file) => {
                      const { url } = await fastapiService.uploadAdminAssetImage(file, 'invoice_page_background');
                      mergeGeneralBg({ image_url: url, image_data_url: '' });
                    }}
                    onClear={() => mergeGeneralBg({ image_data_url: '', image_url: '' })}
                  />
                  <label className="block text-xs font-medium text-slate-700">
                    Image position
                    <select
                      value={config.general.background.image_position}
                      onChange={(e) => mergeGeneralBg({ image_position: e.target.value })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    >
                      {IMAGE_POSITION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config.general.background.use_background_color}
                      onChange={(e) => mergeGeneralBg({ use_background_color: e.target.checked })}
                    />
                    Background color
                    <input
                      value={config.general.background.background_color}
                      onChange={(e) => mergeGeneralBg({ background_color: e.target.value })}
                      className="w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm"
                    />
                    <input
                      type="color"
                      value={
                        /^#[0-9a-f]{6}$/i.test(config.general.background.background_color)
                          ? config.general.background.background_color
                          : '#ffffff'
                      }
                      onChange={(e) => mergeGeneralBg({ background_color: e.target.value })}
                      className="h-8 w-10 cursor-pointer rounded border border-slate-300"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Or image URL (optional, instead of upload)
                    <input
                      value={config.general.background.image_url}
                      onChange={(e) => mergeGeneralBg({ image_url: e.target.value, image_data_url: '' })}
                      placeholder="https://…"
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    />
                  </label>
                </div>
              </AccordionRow>
            </div>
          )}

          {activeTab === 'header_footer' && (
            <div className="mx-auto max-w-3xl space-y-2 pb-10">
              <AccordionRow
                title="Header"
                subtitle="Banner image, position, first-page height"
                open={hfOpen.header}
                onToggle={() => setHfOpen((s) => ({ ...s, header: !s.header }))}
              >
                <div className="space-y-3">
                  {(config.header_footer.header.image_data_url || config.header_footer.header.image_url) && (
                    <div className="relative overflow-hidden rounded-md border border-slate-200">
                      <img
                        src={config.header_footer.header.image_data_url || config.header_footer.header.image_url}
                        alt="Header"
                        className="max-h-32 w-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => mergeHeader({ image_data_url: '', image_url: '' })}
                        className="absolute right-2 top-2 rounded bg-red-600 p-1.5 text-white shadow"
                        aria-label="Remove header image"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                  <ImageDrop
                    compact
                    maxBytes={IMAGE_UPLOAD_MAX_BYTES}
                    hasImage={Boolean(
                      config.header_footer.header.image_data_url || config.header_footer.header.image_url,
                    )}
                    onFile={async (file) => {
                      const { url } = await fastapiService.uploadAdminAssetImage(file, 'invoice_header');
                      mergeHeader({ image_url: url, image_data_url: '' });
                    }}
                    onClear={() => mergeHeader({ image_data_url: '', image_url: '' })}
                  />
                  <label className="block text-xs font-medium text-slate-700">
                    Image position
                    <select
                      value={config.header_footer.header.image_position}
                      onChange={(e) => mergeHeader({ image_position: e.target.value })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    >
                      {IMAGE_POSITION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config.header_footer.header.use_background_color}
                      onChange={(e) => mergeHeader({ use_background_color: e.target.checked })}
                    />
                    Background color
                    <input
                      value={config.header_footer.header.background_color}
                      onChange={(e) => mergeHeader({ background_color: e.target.value })}
                      className="w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm"
                    />
                    <input
                      type="color"
                      value={
                        /^#[0-9a-f]{6}$/i.test(config.header_footer.header.background_color)
                          ? config.header_footer.header.background_color
                          : '#ffffff'
                      }
                      onChange={(e) => mergeHeader({ background_color: e.target.value })}
                      className="h-8 w-10 cursor-pointer rounded border border-slate-300"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Header image URL (optional)
                    <input
                      value={config.header_footer.header.image_url}
                      onChange={(e) => mergeHeader({ image_url: e.target.value, image_data_url: '' })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config.header_footer.header.apply_first_page_only}
                      onChange={(e) => mergeHeader({ apply_first_page_only: e.target.checked })}
                    />
                    Apply to first page only
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Header height first page (in)
                    <input
                      type="number"
                      step={0.1}
                      value={config.header_footer.header.header_height_first_page_in}
                      onChange={(e) => mergeHeader({ header_height_first_page_in: parseFloat(e.target.value) || 0 })}
                      className="mt-1 w-32 rounded border border-slate-300 px-2 py-2 text-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Note under banner (first page)
                    <textarea
                      value={config.header_footer.header.header_content_note}
                      onChange={(e) => mergeHeader({ header_content_note: e.target.value })}
                      rows={2}
                      placeholder="Optional line(s) shown below the header image in preview"
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    />
                  </label>
                  {config.header_footer.header.apply_first_page_only && (
                    <label className="block text-xs font-medium text-slate-700">
                      Note — other pages (stored for PDF; preview shows first page only)
                      <textarea
                        value={config.header_footer.header.header_other_pages_note}
                        onChange={(e) => mergeHeader({ header_other_pages_note: e.target.value })}
                        rows={2}
                        className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                      />
                    </label>
                  )}
                </div>
              </AccordionRow>

              <AccordionRow
                title="Footer"
                subtitle="Footer typography, image strip, page numbers"
                open={hfOpen.footer}
                onToggle={() => setHfOpen((s) => ({ ...s, footer: !s.footer }))}
              >
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-xs font-medium text-slate-700">
                      Font size (pt)
                      <input
                        type="number"
                        min={4}
                        max={20}
                        value={config.header_footer.footer.font_size_pt}
                        onChange={(e) => mergeFooter({ font_size_pt: parseInt(e.target.value, 10) || 6 })}
                        className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                      />
                    </label>
                    <label className="block text-xs font-medium text-slate-700">
                      Font color
                      <div className="mt-1 flex gap-2">
                        <input
                          value={config.header_footer.footer.font_color}
                          onChange={(e) => mergeFooter({ font_color: e.target.value })}
                          className="min-w-0 flex-1 rounded border border-slate-300 px-2 py-2 font-mono text-sm"
                        />
                        <input
                          type="color"
                          value={
                            /^#[0-9a-f]{6}$/i.test(config.header_footer.footer.font_color)
                              ? config.header_footer.footer.font_color
                              : '#aaaaaa'
                          }
                          onChange={(e) => mergeFooter({ font_color: e.target.value })}
                          className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                        />
                      </div>
                    </label>
                  </div>
                  <ImageDrop
                    compact
                    maxBytes={IMAGE_UPLOAD_MAX_BYTES}
                    hasImage={Boolean(
                      config.header_footer.footer.image_data_url || config.header_footer.footer.image_url,
                    )}
                    onFile={async (file) => {
                      const { url } = await fastapiService.uploadAdminAssetImage(file, 'invoice_footer');
                      mergeFooter({ image_url: url, image_data_url: '' });
                    }}
                    onClear={() => mergeFooter({ image_data_url: '', image_url: '' })}
                  />
                  <label className="block text-xs font-medium text-slate-700">
                    Image position
                    <select
                      value={config.header_footer.footer.image_position}
                      onChange={(e) => mergeFooter({ image_position: e.target.value })}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    >
                      {IMAGE_POSITION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-wrap items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config.header_footer.footer.use_background_color}
                      onChange={(e) => mergeFooter({ use_background_color: e.target.checked })}
                    />
                    Background color
                    <input
                      value={config.header_footer.footer.background_color}
                      onChange={(e) => mergeFooter({ background_color: e.target.value })}
                      className="w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm"
                    />
                    <input
                      type="color"
                      value={
                        /^#[0-9a-f]{6}$/i.test(config.header_footer.footer.background_color)
                          ? config.header_footer.footer.background_color
                          : '#ffffff'
                      }
                      onChange={(e) => mergeFooter({ background_color: e.target.value })}
                      className="h-8 w-10 cursor-pointer rounded border border-slate-300"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={config.header_footer.footer.show_page_number}
                      onChange={(e) => mergeFooter({ show_page_number: e.target.checked })}
                    />
                    Page number
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Footer note (above page number in preview)
                    <textarea
                      value={config.header_footer.footer.footer_content_note}
                      onChange={(e) => mergeFooter({ footer_content_note: e.target.value })}
                      rows={2}
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-2 text-sm"
                    />
                  </label>
                </div>
              </AccordionRow>
            </div>
          )}

          {activeTab === 'transaction_details' && (
            <div className="mx-auto max-w-3xl pb-10">
              <AccordionRow
                title="Bill to & invoice meta"
                subtitle="Labels for customer block, dates, terms, and paid-invoice wording (preview + PDF)"
                open={txnOpen}
                onToggle={() => setTxnOpen((v) => !v)}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-xs font-medium text-slate-700">
                    Bill-to heading
                    <input
                      value={td.bill_to_heading}
                      onChange={(e) => mergeTransactionDetails({ bill_to_heading: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Place of supply label
                    <input
                      value={td.place_of_supply_label}
                      onChange={(e) => mergeTransactionDetails({ place_of_supply_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Invoice number label
                    <input
                      value={td.invoice_number_label}
                      onChange={(e) => mergeTransactionDetails({ invoice_number_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Invoice date label
                    <input
                      value={td.invoice_date_label}
                      onChange={(e) => mergeTransactionDetails({ invoice_date_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Terms label
                    <input
                      value={td.terms_label}
                      onChange={(e) => mergeTransactionDetails({ terms_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Due date label
                    <input
                      value={td.due_date_label}
                      onChange={(e) => mergeTransactionDetails({ due_date_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <p className="sm:col-span-2 text-xs text-slate-500">
                    When <strong className="font-medium text-slate-600">Show invoice as paid</strong> is on (General → Document &amp; company), the middle meta column uses the payment labels below.
                  </p>
                  <label className="block text-xs font-medium text-slate-700">
                    Payment / status label (when paid)
                    <input
                      value={td.payment_status_label}
                      onChange={(e) => mergeTransactionDetails({ payment_status_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Paid-in-full text
                    <input
                      value={td.paid_in_full_text}
                      onChange={(e) => mergeTransactionDetails({ paid_in_full_text: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                    Due date placeholder when paid (e.g. em dash)
                    <input
                      value={td.due_date_paid_placeholder}
                      onChange={(e) => mergeTransactionDetails({ due_date_paid_placeholder: e.target.value })}
                      className="mt-1 w-full max-w-xs rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                </div>
              </AccordionRow>
            </div>
          )}

          {activeTab === 'table' && (
            <div className="mx-auto max-w-3xl pb-10">
              <AccordionRow
                title="Line items table"
                subtitle="Column titles; hide second tax column for single-tax layouts"
                open={tblOpen}
                onToggle={() => setTblOpen((v) => !v)}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      ['col_no', 'No. / #'],
                      ['col_description', 'Description column'],
                      ['col_qty', 'Quantity'],
                      ['col_rate', 'Rate'],
                      ['col_cgst', 'First tax column (e.g. CGST)'],
                      ['col_second_tax', 'Second tax column (e.g. UTGST / SGST)'],
                      ['col_amount', 'Amount'],
                    ] as const
                  ).map(([key, title]) => (
                    <label key={key} className="block text-xs font-medium text-slate-700">
                      {title}
                      <input
                        value={tbl[key]}
                        onChange={(e) => mergeTable({ [key]: e.target.value })}
                        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                      />
                    </label>
                  ))}
                  <label className="flex items-center gap-2 text-sm sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={tbl.show_second_tax_column}
                      onChange={(e) => mergeTable({ show_second_tax_column: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Show second tax column
                  </label>
                </div>
              </AccordionRow>
            </div>
          )}

          {activeTab === 'total' && (
            <div className="mx-auto max-w-3xl pb-10">
              <AccordionRow
                title="Totals & amount in words"
                subtitle="Wording next to subtotal, tax lines, total, and paid balance"
                open={totOpen}
                onToggle={() => setTotOpen((v) => !v)}
              >
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                    Amount in words label
                    <input
                      value={tot.total_in_words_label}
                      onChange={(e) => mergeTotal({ total_in_words_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Subtotal label
                    <input
                      value={tot.subtotal_label}
                      onChange={(e) => mergeTotal({ subtotal_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Total label
                    <input
                      value={tot.total_label}
                      onChange={(e) => mergeTotal({ total_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Amount paid label
                    <input
                      value={tot.amount_paid_label}
                      onChange={(e) => mergeTotal({ amount_paid_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Balance due label
                    <input
                      value={tot.balance_due_label}
                      onChange={(e) => mergeTotal({ balance_due_label: e.target.value })}
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700 sm:col-span-2">
                    Zero balance amount (e.g. ₹0.00)
                    <input
                      value={tot.balance_due_zero}
                      onChange={(e) => mergeTotal({ balance_due_zero: e.target.value })}
                      className="mt-1 w-full max-w-xs rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                </div>
              </AccordionRow>
            </div>
          )}

          {activeTab === 'other' && (
            <div className="mx-auto max-w-3xl pb-10">
              <AccordionRow
                title="Remarks & notes"
                subtitle="Optional block on the invoice; bank / pay-to stays under General → Document &amp; company"
                open={othOpen}
                onToggle={() => setOthOpen((v) => !v)}
              >
                <div className="space-y-3">
                  <p className="text-xs text-slate-500">
                    Header banner note and footer note are under <strong className="text-slate-600">Header &amp; Footer</strong>.
                  </p>
                  <label className="block text-xs font-medium text-slate-700">
                    Remarks heading (optional)
                    <input
                      value={config.other.remarks_heading}
                      onChange={(e) => mergeOther({ remarks_heading: e.target.value })}
                      placeholder="e.g. Notes"
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                  <label className="block text-xs font-medium text-slate-700">
                    Remarks body
                    <textarea
                      value={config.other.remarks_body}
                      onChange={(e) => mergeOther({ remarks_body: e.target.value })}
                      rows={5}
                      placeholder="Terms, references, or internal notes (preview shows sample layout only)"
                      className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2 text-sm shadow-sm"
                    />
                  </label>
                </div>
              </AccordionRow>
            </div>
          )}
          </div>
        </main>

        <aside className="hidden max-h-[min(88vh,980px)] w-[36rem] max-w-[calc(100vw-15rem)] shrink-0 overflow-y-auto border-l border-slate-300 bg-white p-4 lg:block xl:w-[44rem] xl:max-w-[calc(100vw-16rem)] 2xl:w-[52rem] 2xl:max-w-[calc(100vw-17rem)]">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Live preview</p>
          <InvoiceTemplatePreview config={config} refreshKey={previewKey} />
        </aside>
        </div>

      <div className="max-h-[45vh] shrink-0 overflow-y-auto border-t border-slate-300 bg-slate-50 p-3 lg:hidden">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Live preview</p>
        <InvoiceTemplatePreview config={config} refreshKey={previewKey} />
      </div>
      </div>
    </div>
  );
};
